import { resetPassword } from "@/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/auth/useAuth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { parseAuthError } from "@/utils/auth-errors";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";

interface FormData {
    newPassword: string;
    confirmNewPassword: string;
}

const ResetPassword = () => {
    const { authenticated } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<FormData>({
        defaultValues: { newPassword: "", confirmNewPassword: "" },
    });

    const { mutate, isPending, error } = useMutation({
        mutationFn: resetPassword,
        onSuccess: () => {
            toast.success("Password reset. Sign in with your new password.");
            navigate("/login");
        },
    });

    const serverError = useMemo(
        () => (error ? parseAuthError(error) : null),
        [error]
    );

    const newPassword = watch("newPassword") || "";

    useDocumentTitle("Reset password");

    useEffect(() => {
        if (authenticated) {
            navigate("/");
        }
    }, [authenticated, navigate]);

    if (!token) {
        return (
            <div className="space-y-7">
                <div>
                    <p className="text-muted-foreground mb-2.5 text-[11px] font-medium tracking-[0.22em] uppercase">
                        Expired
                    </p>
                    <div className="space-y-2">
                        <h1 className="text-3xl font-semibold tracking-tight">
                            Time for a new one
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            Reset links are good for 30 minutes. Request a fresh
                            one and we'll send it right over.
                        </p>
                    </div>
                </div>
                <div className="space-y-3">
                    <Button
                        className="h-10 w-full"
                        render={<Link to="/password/forgot" />}
                    >
                        Request new link
                    </Button>
                    <Link
                        to="/login"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"
                    >
                        <ArrowLeft className="size-3.5" />
                        Back to sign in
                    </Link>
                </div>
            </div>
        );
    }

    const onSubmit = (data: FormData) => {
        mutate({ token, new_password: data.newPassword });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="space-y-7">
                {/* Header */}
                <div className="space-y-2">
                    <h1 className="text-3xl font-semibold tracking-tight">
                        Choose a new password
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        You'll use this to sign in from now on.
                    </p>
                </div>

                {/* Server error */}
                {serverError && (
                    <div
                        role="alert"
                        aria-live="polite"
                        className="border-destructive/30 bg-destructive/[0.06] text-destructive rounded-md border px-3 py-2.5 text-sm"
                    >
                        {serverError.message}
                    </div>
                )}

                {/* Fields */}
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="newPassword">New password</Label>
                        <PasswordInput
                            id="newPassword"
                            autoComplete="new-password"
                            autoFocus
                            aria-invalid={!!errors.newPassword}
                            {...register("newPassword", {
                                required: "New password is required",
                            })}
                        />
                        {errors.newPassword && (
                            <p className="text-destructive text-sm">
                                {errors.newPassword.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="confirmNewPassword">
                            Confirm new password
                        </Label>
                        <PasswordInput
                            id="confirmNewPassword"
                            autoComplete="new-password"
                            aria-invalid={!!errors.confirmNewPassword}
                            {...register("confirmNewPassword", {
                                required: "Please confirm your password",
                                validate: (value) =>
                                    value === newPassword ||
                                    "Passwords do not match",
                            })}
                        />
                        {errors.confirmNewPassword && (
                            <p className="text-destructive text-sm">
                                {errors.confirmNewPassword.message}
                            </p>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="space-y-4">
                    <Button
                        type="submit"
                        disabled={isPending}
                        className="h-10 w-full"
                    >
                        {isPending && <Spinner className="mr-2" />}
                        Reset password
                    </Button>
                    <Link
                        to="/login"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"
                    >
                        <ArrowLeft className="size-3.5" />
                        Back to sign in
                    </Link>
                </div>
            </div>
        </form>
    );
};

export default ResetPassword;
